import { Check, Lock } from "lucide-react";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";
import {
  ASSINATURA_OBRIGATORIA_KIT_ADMISSAO, ASSINATURAS_OPCIONAIS_KIT_ADMISSAO, ASSINATURAS_KIT_ADMISSAO_LABELS,
  DEPARTAMENTO_ASSINATURA_GOOGLE_KIT_ADMISSAO, KIT_ADMISSAO_LABELS, ITENS_KIT_ADMISSAO_VARIAVEIS,
  EQUIPAMENTOS_KIT_ADMISSAO, EQUIPAMENTOS_KIT_ADMISSAO_LABELS, TIPO_KIT_POR_NOTEBOOK_CODIGO,
} from "../../constants/custosGeraisProjeto";

const codigoNotebookDoPerfil = (tipoKit) => ITENS_KIT_ADMISSAO_VARIAVEIS[tipoKit].find((c) => c.startsWith("NOTEBOOK_"));

// Código de premissa (ticket) de cada equipamento marcado — "NOTEBOOK" vira o notebook do perfil.
export function codigosEquipamentosKit(tipoKit, equipamentos) {
  return equipamentos.map((e) => (e === "NOTEBOOK" ? codigoNotebookDoPerfil(tipoKit) : e));
}

// Custo dos equipamentos marcados de 1 kit (compra no mês da admissão).
export function custoEquipamentosKit(tipoKit, equipamentos, ticketPorCodigo) {
  return codigosEquipamentosKit(tipoKit, equipamentos).reduce((acc, c) => acc + (ticketPorCodigo[c] || 0), 0);
}

// Reabrir um kit já lançado para edição (abrirItemOuGrupo/abrirEdicaoItem): equipamentos e perfil
// a partir das linhas do grupo, e a linha "âncora" de onde vêm nome/mês/quantidade — qualquer uma
// que não seja a Google, já que nenhum equipamento é obrigatório. null = só a Google aqui (kit de
// outro departamento / da Nova Contratação), não editável por esta tela.
export function kitExistenteDoGrupo(itensGrupo) {
  const ancora = itensGrupo.find((i) => i.equipamento_codigo !== ASSINATURA_OBRIGATORIA_KIT_ADMISSAO);
  if (!ancora) return null;
  const notebook = itensGrupo.find((i) => i.equipamento_codigo?.startsWith("NOTEBOOK_"));
  return {
    ancora,
    tipo_kit: TIPO_KIT_POR_NOTEBOOK_CODIGO[notebook?.equipamento_codigo] || "atendimento",
    equipamentos: EQUIPAMENTOS_KIT_ADMISSAO.filter((e) => (e === "NOTEBOOK" ? !!notebook : itensGrupo.some((i) => i.equipamento_codigo === e))),
    assinaturas: assinaturasOpcionaisDoGrupo(itensGrupo),
  };
}

function Caixa({ C, marcada }) {
  return (
    <span style={{
      width: 16, height: 16, borderRadius: 4, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      border: `1.5px solid ${marcada ? C.brand : C.border}`, background: marcada ? C.brand : "transparent",
    }}>
      {marcada && <Check size={11} color="#fff" strokeWidth={3} />}
    </span>
  );
}

// "Equipamentos" das modais Lançar Kit Admissão e da Nova Contratação — todos opcionais; o perfil
// do notebook só aparece com o Notebook marcado.
export function EquipamentosKit({ C, equipamentos, onChange, tipoKit, onTipoKit, ticketPorCodigo, equipPorCodigo }) {
  const alternar = (codigo) =>
    onChange(EQUIPAMENTOS_KIT_ADMISSAO.filter((e) => (e === codigo ? !equipamentos.includes(e) : equipamentos.includes(e))));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {EQUIPAMENTOS_KIT_ADMISSAO.map((codigo) => {
        const marcada = equipamentos.includes(codigo);
        const ticket = ticketPorCodigo[codigo === "NOTEBOOK" ? codigoNotebookDoPerfil(tipoKit) : codigo] || 0;
        return (
          <div key={codigo} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              type="button"
              onClick={() => alternar(codigo)}
              style={{
                display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                padding: "8px 12px", borderRadius: 8, fontFamily: "inherit", cursor: "pointer",
                border: `1px solid ${marcada ? C.brand : C.border}`,
                background: marcada ? `${C.brand}18` : "transparent",
              }}
            >
              <Caixa C={C} marcada={marcada} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: marcada ? C.brand : C.text }}>
                {EQUIPAMENTOS_KIT_ADMISSAO_LABELS[codigo]}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>{marcada ? formatCurrency(ticket) : "em estoque"}</span>
            </button>
            {codigo === "NOTEBOOK" && marcada && (
              <div style={{ marginLeft: 26 }}>
                <PerfilNotebookKit C={C} tipoKit={tipoKit} onChange={onTipoKit} equipPorCodigo={equipPorCodigo} />
              </div>
            )}
          </div>
        );
      })}
      <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
        Desmarque o que o departamento já tem em estoque — o item não é orçado.
      </p>
    </div>
  );
}

// Códigos de assinatura de um kit na ordem canônica (Google sempre primeiro) — mesma ordem que o
// backend usa em custos_gerais_projeto_svc.validar_assinaturas_kit_admissao.
export function codigosAssinaturaKit(opcionais) {
  return [ASSINATURA_OBRIGATORIA_KIT_ADMISSAO, ...ASSINATURAS_OPCIONAIS_KIT_ADMISSAO.filter((c) => opcionais.includes(c))];
}

// Infere as assinaturas opcionais de um kit já lançado a partir das linhas do grupo (ao reabrir
// para edição, em abrirItemOuGrupo/abrirEdicaoItem).
export function assinaturasOpcionaisDoGrupo(itensGrupo) {
  return ASSINATURAS_OPCIONAIS_KIT_ADMISSAO.filter((c) => itensGrupo.some((i) => i.equipamento_codigo === c));
}

// Custo das assinaturas do kit no ano: valor mensal × usuários × meses do mês da admissão até Dez.
export function estimativaAssinaturasKit(opcionais, ticketPorCodigo, quantidade, mes) {
  const mensalPorUsuario = codigosAssinaturaKit(opcionais).reduce((acc, c) => acc + (ticketPorCodigo[c] || 0), 0);
  const meses = 12 - mes + 1;
  return { mensalPorUsuario, meses, total: mensalPorUsuario * Number(quantidade || 0) * meses };
}

// "Perfil do notebook" das modais Lançar Kit Admissão e da Nova Contratação (Mão de Obra) — um
// botão por tipo_kit, com especificação e ticket do notebook de Premissas · Equipamentos e Materiais.
export function PerfilNotebookKit({ C, tipoKit, onChange, equipPorCodigo }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {Object.entries(KIT_ADMISSAO_LABELS).map(([valor, label]) => {
        const equip = equipPorCodigo[codigoNotebookDoPerfil(valor)];
        const selecionado = tipoKit === valor;
        return (
          <button
            key={valor}
            type="button"
            onClick={() => onChange(valor)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, textAlign: "left",
              padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
              border: `1px solid ${selecionado ? C.brand : C.border}`,
              background: selecionado ? `${C.brand}18` : "transparent",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: selecionado ? C.brand : C.text }}>{label}</span>
            {equip && (
              <span style={{ fontSize: 11, color: C.muted }}>{equip.especificacao} · {formatCurrency(equip.ticket_medio)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// "Perfil de assinaturas" das modais Lançar Kit Admissão (Despesas Gerais e Custos Gerais por
// Projeto) e da Nova Contratação — Google fixa/obrigatória, Claude/Cursor/Gemini/GitLab alternam.
// `selecionadas` só guarda as opcionais. Com `projetos` (Nova Contratação da OPERAÇÃO), cada
// opcional marcada pede o projeto específico em que será lançada (`projetosAssinatura`).
export default function PerfilAssinaturasKit({ C, selecionadas, onChange, ticketPorCodigo, mes, projetos, projetosAssinatura, onProjetoAssinatura }) {
  const alternar = (codigo) =>
    onChange(selecionadas.includes(codigo) ? selecionadas.filter((c) => c !== codigo) : [...selecionadas, codigo]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[ASSINATURA_OBRIGATORIA_KIT_ADMISSAO, ...ASSINATURAS_OPCIONAIS_KIT_ADMISSAO].map((codigo) => {
        const obrigatoria = codigo === ASSINATURA_OBRIGATORIA_KIT_ADMISSAO;
        const marcada = obrigatoria || selecionadas.includes(codigo);
        return (
          <div key={codigo} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <button
              type="button"
              onClick={obrigatoria ? undefined : () => alternar(codigo)}
              disabled={obrigatoria}
              style={{
                display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                padding: "8px 12px", borderRadius: 8, fontFamily: "inherit",
                cursor: obrigatoria ? "default" : "pointer",
                border: `1px solid ${marcada ? C.brand : C.border}`,
                background: marcada ? `${C.brand}18` : "transparent",
              }}
            >
              <Caixa C={C} marcada={marcada} />
              <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: marcada ? C.brand : C.text, display: "flex", alignItems: "center", gap: 6 }}>
                  {ASSINATURAS_KIT_ADMISSAO_LABELS[codigo]}
                  {obrigatoria && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 600, color: C.muted }}>
                      <Lock size={10} /> obrigatória
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 11, color: C.muted }}>
                  {formatCurrency(ticketPorCodigo[codigo] || 0)}/mês por usuário
                  {obrigatoria && ` · sempre em ${DEPARTAMENTO_ASSINATURA_GOOGLE_KIT_ADMISSAO}`}
                </span>
              </span>
            </button>
            {projetos && marcada && !obrigatoria && (
              <select
                value={projetosAssinatura?.[codigo] ?? ""}
                onChange={(e) => onProjetoAssinatura(codigo, e.target.value ? Number(e.target.value) : null)}
                style={{
                  marginLeft: 26, padding: "6px 8px", borderRadius: 6, fontSize: 12, fontFamily: "inherit",
                  border: `1px solid ${projetosAssinatura?.[codigo] ? C.border : C.danger}`, background: C.card, color: C.text,
                }}
              >
                <option value="">Projeto de {ASSINATURAS_KIT_ADMISSAO_LABELS[codigo]}…</option>
                {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            )}
          </div>
        );
      })}
      <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
        Lançadas em 2.6.11 Assinaturas e Sistemas, de {MESES_ABREV[mes - 1]} a Dez, com o valor mensal de Premissas · Assinaturas e Sistemas.
        {" "}A Google vai sempre para 2.6.11 da {DEPARTAMENTO_ASSINATURA_GOOGLE_KIT_ADMISSAO}.
      </p>
    </div>
  );
}
